import { EsqueletoDePagina, formaDaRota } from '../ui/esqueletos';
import { BarraDoSuite } from '../ui/menu-produto';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Icone, type ComponenteDeIcone } from '../ui/icones';
import { Outlet, useLocation } from 'react-router-dom';
import { NavLink, useNavigate, useIrParaAConta } from '../core/rotas';
import { useAuth, useCan } from '../core/auth';
import { PAPEIS, type AppCode, type Permission } from '../core/permissions';
import { Button } from '../ui';
import { aplicarMarca, logoDaMarca } from '../ui/marca';
import { Marca, MarcaBio } from '../ui/lockup';
import { PRODUTOS } from '../core/produtos';
import { useTheme } from '../ui/theme';
import { ErrorBoundary } from './ErrorBoundary';
import { BuscaGlobal } from './BuscaGlobal';
import { Notificacoes } from './Notificacoes';
import { useOne, type Row } from '../data/db';
import './topo.css';
import { clienteDoHost, PAINEL } from '../core/dominios';

/**
 * `soYard` marca o que é bastidor: instalar produto, chave de API, webhook,
 * recurso, plano e auditoria. O cliente não vê nem alcança, e durante a visita
 * a equipe vê tudo, porque ali quem responde é o administrador de plataforma.
 *
 * `app` amarra o item ao produto instalado: item de produto que a conta não
 * assinou não aparece, mesmo que a pessoa tenha a permissão.
 */
type Item = {
  to: string;
  label: string;
  icon: ComponenteDeIcone;
  perm?: Permission;
  soYard?: boolean;
  app?: AppCode;
  fim?: boolean;
  /** Rotas fora de `to` que também acendem este item (ex.: `/emails` no MKT). */
  tambem?: string[];
};

/**
 * O MENU DO ESPAÇO DE TRABALHO (tela-crm.html, seção 1).
 *
 * O nome do menu é o nome do produto Yard, não o nome genérico do módulo:
 * "Yard CRM" (não "Negócios"), "Yard MKT" (não "Campanhas"), "Flow" (não
 * "Conversas"). "Flow Pop-up" já estava certo.
 *
 * A rota NÃO muda aqui: continua `/crm`, `/mkt`, `/flw`. Trocar o caminho
 * quebraria todo link já salvo e todo favorito, e é assunto de migração, não
 * de tela. O que muda é a palavra que a pessoa lê.
 */
const ESPACO_ITEMS: Item[] = [
  { to: '/home', label: 'Painel', icon: Icone.Painel },
  { to: '/crm', label: 'Yard CRM', icon: Icone.Negocio, perm: 'crm.deal:read', app: 'CRM' },
  { to: '/mkt', label: 'Yard MKT', icon: Icone.Campanha, perm: 'mkt.contact:read', app: 'MKT', tambem: ['/emails'] },
  { to: '/flw', label: 'Flow', icon: Icone.ChatLateral, perm: 'flw.conversation:handle', app: 'FLW' },
  { to: '/pop', label: 'Flow Pop-up', icon: Icone.Balao, perm: 'pop.report:read', app: 'POP' },
];

/**
 * O grupo "Vitrine" do documento. A "Loja" que aparece ali ao lado de
 * `myard.bio` NÃO entra: no banco a loja é de um site (`/bio/sites/:id/produtos`),
 * não existe porta de primeiro nível para ela, e um item de menu que não leva a
 * lugar nenhum é pior do que a ausência dele. Volta quando a rota existir.
 */
const VITRINE_ITEMS: Item[] = [
  { to: '/bio', label: 'Yard Bio', icon: Icone.LinkBio, perm: 'bio.site:read', app: 'BIO' },
];

/**
 * O console da Yard. Não é permissão de organização: é estar na tabela
 * `platform_admins`, que nenhum cliente alcança.
 */
const YARD_ITEMS: { to: string; label: string; icon: ComponenteDeIcone }[] = [
  { to: '/admin', label: 'Visão geral', icon: Icone.Relatorio },
  { to: '/admin/clientes', label: 'Clientes', icon: Icone.Empresa },
  { to: '/admin/usuarios', label: 'Usuários', icon: Icone.Participantes },
  { to: '/admin/equipe', label: 'Equipe Yard', icon: Icone.Escudo },
  { to: '/admin/auditoria', label: 'Auditoria', icon: Icone.Ata },
  { to: '/admin/recursos', label: 'Recursos', icon: Icone.Interruptor },
];

// V44: as cinco de auto-serviço (perfil, usuários, marca, organização,
// segurança) não são guardadas por PERMISSÃO NA ROTA - `ROTAS-CONFIGURACOES.md`
// §5 é explícito: "os campos ficam visíveis e desabilitados, com uma linha
// dizendo quem pode editar - nunca a aba some inteira, porque sumir
// ensinaria que o recurso não existe". A régua e as abas (`Router.tsx`,
// `rotasDaAreaDoCliente`) já seguem essa regra; só este rail continuava
// filtrando por `perm`, contando uma história diferente da tela que abre.
// As seis da equipe Yard continuam com `soYard`: ali a aba nem aparece
// mesmo, de propósito (documento, seção 4).
const SETTINGS_ITEMS: Item[] = [
  { to: '/settings/profile', label: 'Perfil', icon: Icone.Usuario },
  { to: '/settings/members', label: 'Usuários', icon: Icone.Participantes },
  { to: '/settings/apps', label: 'Apps', icon: Icone.Catalogo, soYard: true },
  { to: '/settings/apikeys', label: 'Chaves de API', icon: Icone.Chave, soYard: true },
  { to: '/settings/integrations', label: 'Integrações', icon: Icone.Automacao, soYard: true },
  { to: '/settings/flags', label: 'Recursos', icon: Icone.Interruptor, soYard: true },
  { to: '/settings/branding', label: 'Marca', icon: Icone.Paleta },
  { to: '/settings/organization', label: 'Organização', icon: Icone.Empresa },
  { to: '/settings/security', label: 'Segurança', icon: Icone.Escudo },
  { to: '/settings/billing', label: 'Plano', icon: Icone.Pagamento, soYard: true },
  { to: '/settings/audit', label: 'Auditoria', icon: Icone.Ata, soYard: true },
];

function RailLink({ item, compacto = false }: { item: Item; compacto?: boolean }) {
  // Sem permissão, o item some do menu (documento 17.2).
  const allowed = useCan(item.perm);
  const { adminPlataforma, installedApps } = useAuth();
  const Desenho = item.icon;
  // A biblioteca de e-mails mora em `/emails`, fora de `/mkt`, mas é
  // Marketing: sem isto o item "Yard MKT" apagava ao entrar nela e a pessoa
  // ficava sem saber em que produto estava.
  const caminho = useLocation().pathname;
  const { org } = useAuth();
  const semSlug = org?.slug ? caminho.replace(new RegExp(`^/${org.slug}(?=/|$)`), '') || '/' : caminho;
  if (!allowed || (item.soYard && !adminPlataforma) || (item.app && !installedApps.includes(item.app))) return null;
  const tambemAtivo = (item.tambem ?? []).some((r) => semSlug === r || semSlug.startsWith(r + '/'));
  return (
    <NavLink
      to={item.to}
      aria-label={compacto ? item.label : undefined}
      data-rotulo={compacto ? item.label : undefined}
      end={item.fim ?? item.to === '/admin'}
      className={({ isActive }) => `rail-item${isActive || tambemAtivo ? ' active' : ''}`}
    >
      <span className="ico" aria-hidden="true">
        <Desenho tamanho={16} />
      </span>
      {compacto ? <span className="sr-only">{item.label}</span> : item.label}
    </NavLink>
  );
}

export function Shell() {
  const { profileName, user, org, orgs, role, adminPlataforma, visitando, switchOrg, sairDaOrg, signOut } =
    useAuth();
  const navigate = useNavigate();
  const irParaAConta = useIrParaAConta();
  const qc = useQueryClient();
  const { theme } = useTheme();

  const [menuAberto, setMenuAberto] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [buscaAberta, setBuscaAberta] = useState(false);
  // A foto que a pessoa pôs em Configurações > Perfil (profiles.avatar_url).
  const perfil = useOne<Row & { avatar_url: string | null }>('profiles', user?.id, 'id,avatar_url');
  const [fotoQuebrou, setFotoQuebrou] = useState(false);
  const foto = !fotoQuebrou ? perfil.data?.avatar_url ?? null : null;

  // A cor da organização ativa vale enquanto o painel dela estiver aberto.
  // Ao sair da conta (ou trocar de cliente) o efeito roda de novo e devolve o
  // amarelo da Yard, senão a marca de um cliente vazaria para a tela do outro.
  //
  // V29: `theme` nas dependências - sem isto, trocar de tema com uma marca
  // escura aplicada mantinha os tokens calculados para o tema anterior até
  // a próxima troca de organização.
  useEffect(() => {
    aplicarMarca(org?.accent, org?.accent_alt, org?.brand_radius, theme);
    return () => aplicarMarca(null);
  }, [org?.accent, org?.accent_alt, org?.brand_radius, theme]);

  // A aba do navegador também é marca: quem tem dez abas abertas reconhece o
  // painel pelo ícone, não pelo título cortado.
  useEffect(() => {
    const icone = org?.logo_url || org?.logo_dark;
    if (!icone) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    // V33/V34: quando o `<link>` não existia, `anterior` capturava o href
    // vazio do elemento recém-criado - a limpeza devolvia um `<link
    // rel="icon" href="">` solto no `<head>`, em vez de tirar o elemento que
    // esta injeção criou.
    const linkJaExistia = !!link;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const anterior = link.href;
    link.href = icone;
    return () => {
      if (linkJaExistia) {
        if (link) link.href = anterior;
      } else {
        link?.remove();
      }
    };
  }, [org?.logo_url, org?.logo_dark]);



  /* Os atalhos da busca: ⌘K / Ctrl+K em qualquer lugar (o do YardHub) e "/"
     (o do documento, seção 1). A barra só quando o foco NÃO está num campo -
     senão digitar uma barra dentro de "Buscar por nome" roubaria o cursor. */
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setMenuAberto(false);
        setBuscaAberta(true);
        return;
      }
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const alvo = e.target as HTMLElement | null;
      const tag = alvo?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || alvo?.isContentEditable) return;
      e.preventDefault();
      setBuscaAberta(true);
    }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, []);

  /* O menu da conta fecha no Esc e no clique fora - é menu, não gaveta. */
  useEffect(() => {
    if (!menuAberto) return;
    function aoClicar(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuAberto(false);
    }
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuAberto(false);
    }
    document.addEventListener('mousedown', aoClicar);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicar);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [menuAberto]);

  // A equipe Yard pode não ter organização própria: só o console faz sentido.
  const semOrganizacao = !org;
  const rota = useLocation();

  // C1: "Console da Yard" é sobre QUEM é a pessoa, não sobre `org` estar
  // vazio. Antes um cliente suspenso que caísse aqui por causa do laço da
  // auditoria (org nulo, mas ele não é da equipe) via a copy da Yard - o
  // cliente errado com a marca errada. Com C1 corrigido esse caso não chega
  // mais até o Shell, mas a decisão continua amarrada ao dado certo.
  const nomeNoTopo = org?.name ?? (adminPlataforma ? 'Console da Yard' : 'Sua conta');

  // A3: trocar de organização é trocar de host. Antes o `navigate('/home')`
  // rodava no host do cliente A e o RouteGuard, vendo URL=A e sessão=B,
  // desfazia a troca sozinho - cabo de guerra que só terminava travado em
  // "Abrindo a conta...". Com o host mudando junto (`useIrParaAConta`), URL e
  // sessão nunca chegam a discordar; a reconciliação do RouteGuard vira rede
  // de segurança de dev, não o caminho normal.
  async function trocarOrg(id: string) {
    const alvo = orgs.find((o) => o.id === id);
    if (!alvo) return;
    // Troca de tenant descarta todo o cache do anterior (documento 18.2).
    qc.clear();
    setMenuAberto(false);
    await switchOrg(id);
    await irParaAConta(alvo.slug, '/home');
  }

  /* Uma tela, uma família de cor (contrato, seção 3). A família sai da rota:
     amarelo na plataforma e no CRM, roxo no Marketing, verde na vitrine, ciano
     na conversa. Ela NÃO mexe em `--accent`, que é a cor do cliente - pinta só
     o que é da Yard: o filete da aba ativa e a marca do olho de seção.

     `rota.pathname` é o caminho cru do navegador, com o slug do cliente na
     frente (`/weareyard/bio/sites/...` - core/rotas.tsx, comSlug). Comparar
     isso direto contra `p.rota` ("/bio") sem tirar o slug nunca batia - só
     não aparecia porque o produto mais visitado no dia a dia (CRM) tem
     família "plataforma", igual ao fallback deste `?? `, e o erro se escondeu
     atrás da coincidência. Qualquer produto de outra família (Bio/vitrine,
     Marketing/demanda, Flow/conversa) sempre caía no amarelo de plataforma. */
  const semSlug = org?.slug ? rota.pathname.replace(new RegExp(`^/${org.slug}(?=/|$)`), '') || '/' : rota.pathname;
  const produtoDaRota = PRODUTOS.find((p) =>
    [p.rota, ...(p.rotasExtras ?? [])].some((r) => semSlug === r || semSlug.startsWith(r + '/')),
  );
  const familiaDaRota = produtoDaRota?.familia ?? 'plataforma';

  /* O SELO DE FAMÍLIA (tela-crm.html, seção 1: `yard` + a pílula `crm`).
     A pílula diz em que produto a pessoa está. Cravada em "suite", ela dizia
     sempre a mesma coisa - ou seja, não dizia nada.

     `prefixo` e `mesclada` vêm do catálogo (`core/produtos.ts`), porque as
     exceções são fixas por produto, não por rota: o pop-up flow troca "yard"
     por "pop-up" na frente, e o myard.bio nem tem prefixo separado - é uma
     palavra só (`MarcaBio`). Sem isso os dois caíam no fallback "yard suite"
     dentro de Bio e Pop-up, que é produto errado (marca.html, 02.2). */
  const selo = produtoDaRota?.sufixo || 'suite';
  const prefixoDaRota = produtoDaRota?.prefixo ?? 'yard';
  const mescladaDaRota = !!produtoDaRota?.mesclada;


  // O bloco da conta do topo, como no YardHub: primeiro nome em cima e o
  // papel embaixo ("Dono", "Atendente"...; a equipe Yard sem papel na conta
  // é "Equipe Yard"), avatar com a foto ou as iniciais.
  const primeiroNome = (profileName || '').trim().split(/\s+/)[0] || 'Minha conta';
  const papelNoTopo = PAPEIS.find((p) => p.value === role)?.label ?? (adminPlataforma ? 'Equipe Yard' : 'Sua conta');
  const iniciaisPessoa = (profileName || user?.email || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();


  return (
    <div className="shell suite-modern" data-familia={familiaDaRota}>
      {/* O rail lateral saiu na rodada 2 da F1 (26/10/2026), a pedido da F4: na
          `.suite-modern` ele nunca aparecia (`.shell.suite-modern>.rail` é
          `display:none`) e só repetia, escondido, as listas de Configurações
          e do Console. A navegação é a pílula (`DockDoSuite`), e o "Mais"
          dela é a porta de Configurações e do Console. */}

      <div>
        {/* Enquanto a Yard está dentro da conta de um cliente, isto fica no
            topo de toda tela: é fácil esquecer de quem é o dado que se está
            olhando, e um clique errado aqui é um clique na conta de outra
            pessoa. Cada entrada e cada saída fica no audit_logs do cliente. */}
        {visitando && (
          <div className="faixa-visita">
            <span>
              Você está dentro da conta de <strong>{visitando.name}</strong>. Tudo o que
              fizer aqui é como se fosse o cliente.
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                qc.clear();
                await sairDaOrg();
                // A6: `navigate` (core/rotas) não troca de host - na porta do
                // cliente (`cima.yardsuite.com.br`) isso deixava a barra de
                // endereço presa lá, e `/admin/clientes` sem prefixo nenhum
                // cai no `path="*"` do bloco de rotas do cliente (404). Só
                // sai de verdade trocando de host quando é nele que se está.
                if (clienteDoHost(window.location.host)) {
                  window.location.href = `${PAINEL}/admin/clientes`;
                } else {
                  navigate('/admin/clientes');
                }
              }}
            >
              Sair da conta
            </Button>
          </div>
        )}

        <header className="topbar">
          <NavLink to={semOrganizacao ? '/admin' : '/home'} className="suite-topo-marca" aria-label="Página inicial do Suite">
            {logoDaMarca(org, theme) && <img src={logoDaMarca(org, theme)} alt={org?.name} />}
            <span className="suite-marca-transicao" key={produtoDaRota?.code ?? 'suite'} data-familia={familiaDaRota}>
              {mescladaDaRota ? <MarcaBio tamanho={28} /> : <Marca produto={selo} prefixo={prefixoDaRota} tamanho={28} />}
            </span>
          </NavLink>
          <div className="topo-acoes">
            {/* A busca do YardHub: pílula "Buscar ⌘K" que abre o diálogo
                (src/shell/BuscaGlobal.tsx). Antes era um campo que recebia o
                texto e não levava a lugar nenhum. */}
            <button
              type="button"
              className="topo-busca"
              aria-label="Buscar no Suite"
              aria-keyshortcuts="Meta+K Control+K /"
              aria-haspopup="dialog"
              onClick={() => { setMenuAberto(false); setBuscaAberta(true); }}
            >
              <Icone.Busca tamanho={17} aria-hidden="true" />
              <span>Buscar</span>
              <kbd aria-hidden="true">⌘ K</kbd>
            </button>

            {!semOrganizacao && <Notificacoes />}

            <div className="topo-conta" ref={menuRef}>
              <button
                className="topo-conta__botao"
                type="button"
                aria-label={`Conta de ${profileName || 'você'}`}
                aria-haspopup="menu"
                aria-expanded={menuAberto}
                aria-controls="menu-da-conta"
                onClick={() => setMenuAberto((v) => !v)}
              >
                <span className="topo-conta__nome">
                  <b>{primeiroNome}</b>
                  <small>{papelNoTopo}</small>
                </span>
                <span className="topo-conta__avatar">
                  {foto
                    ? <img src={foto} alt="" onError={() => setFotoQuebrou(true)} />
                    : <span aria-hidden="true">{iniciaisPessoa}</span>}
                </span>
                <Icone.ChevronBaixo tamanho={13} aria-hidden="true" />
              </button>

              {menuAberto && (
                <div id="menu-da-conta" className="topo-conta__menu" role="menu">
                  {/* Quem é e DE QUEM é esta tela: o nome do cliente fica aqui,
                      junto da troca de conta, porque errar de cliente custa
                      disparo na base errada (TELA-PAINEL.md, seção 1). */}
                  <div className="topo-conta__resumo">
                    <b>{profileName || 'Sua conta'}</b>
                    {user?.email && <span>{user.email}</span>}
                    <span className="topo-conta__org">{nomeNoTopo}</span>
                  </div>
                  {/* A5: trocar de organização durante uma visita não fazia
                      nada visível - `switchOrg` grava `active_org`, mas
                      `impersonando` continua valendo na frente dele
                      (`current_org()`, 0019:80) e a tela não sai da conta do
                      cliente. Esconder a seção evita a ilusão de que a troca
                      aconteceu; quem quer trocar de verdade sai da visita
                      primeiro, em "Sair da conta" na faixa acima. */}
                  {orgs.length > 1 && !visitando && (
                    <>
                      <div className="topo-conta__rot">Trocar de conta</div>
                      {orgs.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          role="menuitem"
                          aria-current={o.id === org?.id}
                          onClick={() => void trocarOrg(o.id)}
                        >
                          <Icone.Empresa tamanho={15} aria-hidden="true" />
                          {o.name}
                        </button>
                      ))}
                    </>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setMenuAberto(false); navigate('/settings/profile'); }}
                  >
                    <Icone.Usuario tamanho={15} aria-hidden="true" />
                    Minha conta
                  </button>
                  {!semOrganizacao && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setMenuAberto(false); navigate('/settings/organization'); }}
                    >
                      <Icone.Engrenagem tamanho={15} aria-hidden="true" />
                      Configurações
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    className="topo-conta__sair"
                    onClick={async () => {
                      setMenuAberto(false);
                      qc.clear();
                      await signOut();
                      navigate('/login', { replace: true });
                    }}
                  >
                    <Icone.SetaDireita tamanho={15} aria-hidden="true" />
                    Sair da conta
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <BuscaGlobal aberto={buscaAberta} aoFechar={() => setBuscaAberta(false)} />

        <main className="content">
          <ErrorBoundary area="shell">
            {/* A troca de tela espera aqui dentro: topo e dock não somem
                enquanto o pedaço da próxima tela chega. */}
            <Suspense fallback={<EsqueletoDePagina forma={formaDaRota(rota.pathname)} />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <DockDoSuite semOrganizacao={semOrganizacao} adminPlataforma={adminPlataforma} />
    </div>
  );
}


/** Navegação Hub em pílula, usando as rotas e permissões reais do Suite. */
export function DockDoSuite({ semOrganizacao, adminPlataforma }: { semOrganizacao: boolean; adminPlataforma: boolean }) {
  const detalhes = useRef<HTMLDetailsElement>(null);
  const rota = useLocation();
  useEffect(() => { if (detalhes.current) detalhes.current.open = false; }, [rota.pathname]);
  useEffect(() => {
    const fechar = (evento: MouseEvent) => { if (detalhes.current && !detalhes.current.contains(evento.target as Node)) detalhes.current.open = false; };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, []);
  const produtos: Item[] = [
    ...ESPACO_ITEMS.filter(i => i.to !== '/pop').map(i => ({...i, label:i.to === '/home' ? 'Início' : i.to === '/crm' ? 'CRM' : i.to === '/mkt' ? 'Marketing' : i.label})),
    ...VITRINE_ITEMS.map(i => ({...i,label:'Bio'})),
    {to:'/mail',label:'Mail',icon:Icone.Enviados,app:'MAIL'},
  ];
  return <BarraDoSuite><nav className="suite-dock" aria-label="Navegação principal">
    <div className="suite-dock__atalhos">{(semOrganizacao ? YARD_ITEMS.slice(0,4) : produtos).map(item => <RailLink key={item.to} item={item} compacto />)}</div>
    <details className="suite-dock__mais" ref={detalhes} onKeyDown={e => { if(e.key === 'Escape' && detalhes.current) { detalhes.current.open=false; detalhes.current.querySelector('summary')?.focus(); } }}>
      <summary aria-label="Mais produtos e configurações" data-rotulo="Mais"><Icone.Reticencias tamanho={20} aria-hidden="true" /><span className="sr-only">Mais</span></summary>
      <div className="suite-dock__menu" onClick={e => { if ((e.target as HTMLElement).closest('a') && detalhes.current) detalhes.current.open = false; }}>
        {!semOrganizacao && <><span className="suite-dock__grupo">Seus produtos</span>{[...produtos,...ESPACO_ITEMS.filter(i=>i.to==='/pop')].map(item=><RailLink key={item.to} item={item} />)}<span className="suite-dock__grupo">Sua conta</span>{SETTINGS_ITEMS.map(item=><RailLink key={item.to} item={item} />)}</>}
        {semOrganizacao && <><span className="suite-dock__grupo">Sua conta</span>{SETTINGS_ITEMS.filter(i => ['/settings/profile','/settings/security'].includes(i.to)).map(item=><RailLink key={item.to} item={item} />)}</>}
        {adminPlataforma && <><span className="suite-dock__grupo">Console Yard</span>{YARD_ITEMS.map(item=><RailLink key={item.to} item={item} />)}</>}
      </div>
    </details>
  </nav></BarraDoSuite>;
}
